import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import { Creature } from '../../types';
import { PopoverState } from '../../hooks/useItemPopover';
import { formatSoldierType } from '../../utils/formatting';
import { createSubtleIceSurface, hashStringToSeed } from '../../theme/surfaces';
import { ItemDetailsPopover } from './ItemDetailsPopover';
import { StatColumn } from './StatColumn';

interface CreatureCardProps {
  creature: Creature;
}

export const CreatureCard = React.memo(function CreatureCard({ creature }: CreatureCardProps) {
  const [traitPopover, setTraitPopover] = React.useState<PopoverState | null>(null);
  const iceSurfaceSx = React.useMemo(
    () => createSubtleIceSurface(hashStringToSeed(creature.id), {}),
    [creature.id]
  );

  return (
    <Card variant="outlined" sx={{ ...iceSurfaceSx }}>
      <CardContent sx={{ height: '100%' }}>
        <Stack spacing={1} sx={{ height: '100%' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {creature.name}
          </Typography>
          <Typography component="div" variant="body2" color="text.secondary">
            Type: {formatSoldierType(creature.type, true)}
            {creature.isCustom && <Chip size="small" color="secondary" label="Custom" sx={{ ml: 1 }} />}
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(3, 1fr)', sm: 'repeat(6, 1fr)' },
              gap: 1,
              mt: 0.5,
            }}
          >
            <StatColumn label="Move" value={creature.move} adjustedValue={creature.move} />
            <StatColumn label="Fight" value={creature.fight} adjustedValue={creature.fight} />
            <StatColumn label="Shoot" value={creature.shoot} adjustedValue={creature.shoot} />
            <StatColumn label="Armour" value={creature.armour} adjustedValue={creature.armour} />
            <StatColumn label="Will" value={creature.will} adjustedValue={creature.will} />
            <StatColumn label="Health" value={creature.health} adjustedValue={creature.health} />
          </Box>

          <Typography variant="body2" color="text.secondary">
            Cost: {creature.cost}gc · Item slots: {creature.itemSlot} · Carries treasure: {creature.canCarryTreasure ? 'Yes' : 'No'}
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
              gap: 2,
              alignItems: 'start',
            }}
          >
            <Stack spacing={1.5}>
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Default Weapons</Typography>
                {creature.weapons.length > 0 ? (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {creature.weapons.map((weapon) => <Chip key={weapon.id} size="small" label={weapon.name} />)}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">None</Typography>
                )}
              </Box>

              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Default Armour</Typography>
                {creature.armourItems.length > 0 ? (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {creature.armourItems.map((armour) => <Chip key={armour.id} size="small" label={armour.name} />)}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">None</Typography>
                )}
              </Box>

              {creature.treasureItems.length > 0 && (
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Default Items</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {creature.treasureItems.map((item) => <Chip key={item.id} size="small" label={item.name} />)}
                  </Box>
                </Box>
              )}

              {creature.traits.length > 0 && (
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Traits</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {creature.traits.map((trait) => (
                      <Chip
                        key={trait.id}
                        size="small"
                        label={trait.name}
                        onClick={(event) => setTraitPopover({
                          anchor: event.currentTarget,
                          name: trait.name,
                          description: trait.description,
                          tags: [],
                        })}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Stack>

            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Notes</Typography>
              <Typography variant="body2">{creature.notes || '-'}</Typography>
            </Box>
          </Box>
        </Stack>
      </CardContent>
      <ItemDetailsPopover popover={traitPopover} onClose={() => setTraitPopover(null)} />
    </Card>
  );
});